import type { FC } from 'react'

import { useEffect, useLayoutEffect, useRef } from 'react'
import { prefersReducedMotion } from '@/hooks/use-reduced-motion'

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

const GLYPHS = '0123456789abcdef'
const KEEP = new Set([' ', '…', '·', '.', 'π'])

interface ScrambleTextProps {
    value: string
    duration?: number
    className?: string
}

/** Resolves text out of random hex, like a value being computed in front of you. */
const ScrambleText: FC<ScrambleTextProps> = ({ value, duration = 560, className }) => {
    const ref = useRef<HTMLSpanElement>(null)
    const previous = useRef(value)

    useIsomorphicLayoutEffect(() => {
        const node = ref.current?.firstChild
        const changed = previous.current !== value
        previous.current = value

        if (!node || !changed || !value || prefersReducedMotion()) return

        const began = performance.now()
        let frame = 0

        const step = (now: number) => {
            const progress = Math.min(1, (now - began) / duration)
            const settled = Math.floor(progress * value.length)
            let out = value.slice(0, settled)

            for (let i = settled; i < value.length; i++) {
                const char = value[i]
                out += KEEP.has(char) ? char : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
            }

            node.nodeValue = out
            if (progress < 1) frame = requestAnimationFrame(step)
        }

        frame = requestAnimationFrame(step)

        return () => {
            cancelAnimationFrame(frame)
            node.nodeValue = value
        }
    }, [value])

    return (
        <span ref={ref} className={className}>
            {value || ' '}
        </span>
    )
}

export default ScrambleText
